// frontend/src/components/Auth/Registration/StudentInfo.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../../api/client';
import './StudentInfo.css';

const StudentInfo = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        grade: '',
        stream: '',
        password: '',
        confirmPassword: ''
    });
    const [selectedStream, setSelectedStream] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [passwordStrength, setPasswordStrength] = useState({
        score: 0,
        message: '',
        color: ''
    });
    const [passwordsMatch, setPasswordsMatch] = useState(false);
    const [floatingParticles, setFloatingParticles] = useState([]);

    // Generate floating particles for gamified effect
    useEffect(() => {
        const particles = [];
        for (let i = 0; i < 30; i++) {
            particles.push({
                id: i,
                left: Math.random() * 100,
                delay: Math.random() * 8,
                duration: 5 + Math.random() * 8,
                size: 2 + Math.random() * 6,
                opacity: 0.3 + Math.random() * 0.5
            });
        }
        setFloatingParticles(particles);
    }, []);

    const grades = [
        { value: '1', label: 'Grade 1', desc: 'Beginner Level', xp: '100 XP' },
        { value: '2', label: 'Grade 2', desc: 'Foundation', xp: '150 XP' },
        { value: '3', label: 'Grade 3', desc: 'Building Blocks', xp: '200 XP' },
        { value: '4', label: 'Grade 4', desc: 'Intermediate', xp: '250 XP' },
        { value: '5', label: 'Grade 5', desc: 'Advanced Primary', xp: '300 XP' },
        { value: '6', label: 'Grade 6', desc: 'Pre-Secondary', xp: '350 XP' },
        { value: '7', label: 'Grade 7', desc: 'Core Concepts', xp: '400 XP' },
        { value: '8', label: 'Grade 8', desc: 'Advanced Middle', xp: '450 XP' },
        { value: '9', label: 'Grade 9', desc: 'High School Start', xp: '500 XP' },
        { value: '10', label: 'Grade 10', desc: 'Board Preparation', xp: '600 XP' },
        { value: '11', label: 'Grade 11', desc: 'Pre-University', xp: '700 XP' },
        { value: '12', label: 'Grade 12', desc: 'Final Year', xp: '800 XP' }
    ];

    const checkPasswordStrength = (password) => {
        let score = 0;
        
        if (password.length >= 8) score++;
        if (/[a-zA-Z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (password.length >= 12) score++;
        if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
        
        let message = '';
        let color = '';
        
        if (score < 3) {
            message = 'Weak - Need more power! ⚡';
            color = '#f97316';
        } else if (score < 5) {
            message = 'Good - Getting stronger! 💪';
            color = '#eab308';
        } else {
            message = 'Legendary - Ultimate defense! 🏆';
            color = '#22c55e';
        }
        
        setPasswordStrength({ score, message, color });
    };

    const handlePasswordChange = (e) => {
        const password = e.target.value;
        setFormData({ ...formData, password });
        checkPasswordStrength(password);
        checkPasswordsMatch(password, formData.confirmPassword);
    };

    const handleConfirmPasswordChange = (e) => {
        const confirmPassword = e.target.value;
        setFormData({ ...formData, confirmPassword });
        checkPasswordsMatch(formData.password, confirmPassword);
    };

    const checkPasswordsMatch = (password, confirm) => {
        setPasswordsMatch(password === confirm && password.length > 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.grade) {
            setError('⚔️ Choose your grade to begin the quest!');
            return;
        }
        
        if (!selectedStream) {
            setError('📜 Select your educational board to unlock your path!');
            return;
        }
        
        if (!formData.password || formData.password.length < 8) {
            setError('🔐 Password must be at least 8 characters strong!');
            return;
        }
        
        if (!/[a-zA-Z]/.test(formData.password)) {
            setError('📝 Password needs at least one letter!');
            return;
        }
        
        if (!/\d/.test(formData.password)) {
            setError('🔢 Password needs at least one number!');
            return;
        }
        
        if (formData.password !== formData.confirmPassword) {
            setError('❌ Passwords don\'t match! Double-check your ancient runes.');
            return;
        }
        
        setLoading(true);
        setError('');
        
        try {
            const response = await authAPI.studentInfo({
                grade: formData.grade,
                stream: selectedStream,
                password: formData.password,
                confirm_password: formData.confirmPassword
            });
            
            if (response.success) {
                navigate('/avatar-selection');
            } else {
                setError(response.message || 'Quest failed. Try again!');
            }
        } catch (err) {
            setError('🌐 Network interference! Please reconnect to the realm.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="student-container">
            {/* Floating particles background - gamified effect */}
            <div className="particle-field">
                {floatingParticles.map(particle => (
                    <div
                        key={particle.id}
                        className="particle"
                        style={{
                            left: `${particle.left}%`,
                            top: `${Math.random() * 100}%`,
                            width: `${particle.size}px`,
                            height: `${particle.size}px`,
                            animationDelay: `${particle.delay}s`,
                            animationDuration: `${particle.duration}s`,
                            opacity: particle.opacity
                        }}
                    />
                ))}
            </div>

            {/* Glowing orbs effect */}
            <div className="glow-orb orb-1"></div>
            <div className="glow-orb orb-2"></div>
            <div className="glow-orb orb-3"></div>

            <div className="student-card">
                <div className="student-header">
                    <div className="header-icon">
                        <i className="fas fa-dragon"></i>
                        <span className="xp-badge">+500 XP</span>
                    </div>
                    <h2>Hero's Journey</h2>
                    <p>Forge your path to greatness, young adventurer!</p>
                    <div className="quest-progress">
                        <span className="quest-step">⚔️ Quest 4/5</span>
                        <div className="mini-progress">
                            <div className="mini-progress-fill" style={{ width: '75%' }}></div>
                        </div>
                    </div>
                </div>

                <div className="student-body">
                    <form onSubmit={handleSubmit}>
                        {/* Grade Selection - Gamified Cards */}
                        <div className="form-group gamified-group">
                            <label className="floating-label">
                                <i className="fas fa-crown"></i> Choose Your Rank
                            </label>
                            <div className="grade-carousel">
                                <select
                                    value={formData.grade}
                                    onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                                    className="gamified-select"
                                    required
                                >
                                    <option value="">⚡ Select your grade level</option>
                                    {grades.map(grade => (
                                        <option key={grade.value} value={grade.value}>
                                            {grade.label} - {grade.desc} [{grade.xp}]
                                        </option>
                                    ))}
                                </select>
                                <div className="select-glow"></div>
                            </div>
                            <small className="hint-text">
                                <i className="fas fa-chart-line"></i> Higher grade = more epic challenges!
                            </small>
                        </div>

                        {/* Stream Selection - Interactive Cards */}
                        <div className="form-group">
                            <label className="floating-label">
                                <i className="fas fa-landmark"></i> Choose Your Path
                            </label>
                            <div className="stream-grid">
                                <div
                                    className={`stream-card ${selectedStream === 'CBSE' ? 'selected' : ''}`}
                                    onClick={() => setSelectedStream('CBSE')}
                                >
                                    <div className="stream-icon cbse">
                                        <i className="fas fa-book-open"></i>
                                    </div>
                                    <h6>CBSE</h6>
                                    <p>Central Board of Secondary Education</p>
                                    <div className="stream-badge">🎯 Nationwide</div>
                                    {selectedStream === 'CBSE' && (
                                        <div className="check-mark">
                                            <i className="fas fa-check"></i>
                                        </div>
                                    )}
                                </div>
                                <div
                                    className={`stream-card ${selectedStream === 'ICSE' ? 'selected' : ''}`}
                                    onClick={() => setSelectedStream('ICSE')}
                                >
                                    <div className="stream-icon icse">
                                        <i className="fas fa-globe"></i>
                                    </div>
                                    <h6>ICSE</h6>
                                    <p>Indian Certificate of Secondary Education</p>
                                    <div className="stream-badge">🌍 Global Focus</div>
                                    {selectedStream === 'ICSE' && (
                                        <div className="check-mark">
                                            <i className="fas fa-check"></i>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Password Section - RPG Style */}
                        <div className="password-section">
                            <div className="section-header">
                                <span className="section-icon">🛡️</span>
                                <h5>Forge Your Guardian Seal</h5>
                                <span className="legendary-tag">Legendary Protection</span>
                            </div>

                            <div className="form-group">
                                <label><i className="fas fa-key"></i> Create Magic Password</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type="password"
                                        value={formData.password}
                                        onChange={handlePasswordChange}
                                        placeholder="••••••••"
                                        autoComplete="new-password"
                                        required
                                    />
                                    <i className={`fas ${formData.password ? 'fa-shield-alt' : 'fa-lock'}`}></i>
                                </div>
                                {formData.password && (
                                    <div className="password-strength" style={{ color: passwordStrength.color }}>
                                        <i className={`fas ${passwordStrength.score >= 3 ? 'fa-dragon' : 'fa-exclamation-triangle'}`}></i>
                                        {passwordStrength.message}
                                        <div className="strength-bar">
                                            <div className="strength-fill" style={{ width: `${(passwordStrength.score / 5) * 100}%`, background: passwordStrength.color }}></div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label><i className="fas fa-check-double"></i> Confirm Ancient Rune</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type="password"
                                        value={formData.confirmPassword}
                                        onChange={handleConfirmPasswordChange}
                                        placeholder="••••••••"
                                        autoComplete="new-password"
                                        required
                                    />
                                    <i className={`fas ${passwordsMatch && formData.confirmPassword ? 'fa-check-circle' : 'fa-sync-alt'}`}></i>
                                </div>
                                {formData.confirmPassword && (
                                    <div className={`password-match ${passwordsMatch ? 'match' : 'no-match'}`}>
                                        {passwordsMatch ? (
                                            <><i className="fas fa-shield-hog"></i> Runes aligned! Protection active</>
                                        ) : (
                                            <><i className="fas fa-skull"></i> Runes don't match! Try again</>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="password-requirements">
                                <div className={`requirement ${formData.password.length >= 8 ? 'met' : ''}`}>
                                    <i className={`fas ${formData.password.length >= 8 ? 'fa-check-circle' : 'fa-circle'}`}></i>
                                    <span>Min 8 characters</span>
                                </div>
                                <div className={`requirement ${/[a-zA-Z]/.test(formData.password) ? 'met' : ''}`}>
                                    <i className={`fas ${/[a-zA-Z]/.test(formData.password) ? 'fa-check-circle' : 'fa-circle'}`}></i>
                                    <span>Contains letter</span>
                                </div>
                                <div className={`requirement ${/\d/.test(formData.password) ? 'met' : ''}`}>
                                    <i className={`fas ${/\d/.test(formData.password) ? 'fa-check-circle' : 'fa-circle'}`}></i>
                                    <span>Contains number</span>
                                </div>
                                <div className={`requirement ${/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'met' : ''}`}>
                                    <i className={`fas ${/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'fa-check-circle' : 'fa-circle'}`}></i>
                                    <span>Special rune</span>
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div className="error-message">
                                <i className="fas fa-skull"></i> {error}
                            </div>
                        )}

                        <div className="progress-section">
                            <div className="progress-bar">
                                <div className="progress-fill" style={{ width: '75%' }}></div>
                            </div>
                            <p className="progress-text">
                                <i className="fas fa-map-marker-alt"></i> Step 4 of 5 - Almost at the portal!
                            </p>
                        </div>

                        <button type="submit" className="submit-btn" disabled={loading}>
                            {loading ? (
                                <><i className="fas fa-spinner fa-spin"></i> Summoning Hero...</>
                            ) : (
                                <><i className="fas fa-rocket"></i> Complete the Quest!</>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default StudentInfo;